'use client';

// ============================================================================
// LOCATION AUTOCOMPLETE
// Lightweight address search input using Photon API (free, no API key).
// Captures name, formatted address, and lat/lng from the selected place.
// Usage: appointment location override, task location, etc.
// Does NOT save to contact_locations — caller stores data on the work item.
// ============================================================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Search, X, Loader2, MapPin, Sparkles } from 'lucide-react';
import { geocodeSearchFull, type GeocodeFeature, type GeocodeQuality } from '@/lib/geocoding/search';

export interface CustomLocationData {
  name: string;
  address: string;
  lat: string;
  lng: string;
}

interface PhotonFeature {
  properties: {
    name?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
    city?: string;
    state?: string;
    country?: string;
    osm_value?: string;
    display_name?: string;
  };
  geometry: {
    coordinates: [number, number]; // [lng, lat]
  };
}

interface LocationAutocompleteProps {
  value: string;
  onChange: (val: string) => void;
  onPlaceSelect: (data: CustomLocationData) => void;
  onClear: () => void;
  isDark?: boolean;
  placeholder?: string;
  className?: string;
  proximity?: {
    latitude: number;
    longitude: number;
  };
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelect,
  onClear,
  isDark = false,
  placeholder = 'Search address or place... (e.g., Builder Warehouse Rivonia)',
  className = '',
  proximity,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const [isLoading, setIsLoading] = useState(false);
  const [isIntensifying, setIsIntensifying] = useState(false);
  const [results, setResults] = useState<PhotonFeature[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [quality, setQuality] = useState<GeocodeQuality>('low');
  const [resultSource, setResultSource] = useState<'free' | 'google'>('free');
  const lastQueryRef = useRef('');

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  const performSearch = useCallback(async (query: string, intensify = false) => {
    if (query.trim().length < 3) {
      setResults([]);
      setQuality('low');
      return;
    }
    if (intensify) {
      setIsIntensifying(true);
    } else {
      setIsLoading(true);
    }
    try {
      const { features, quality: q, source } = await geocodeSearchFull(query, 5, proximity, intensify);
      setResults(features as PhotonFeature[]);
      setQuality(q);
      setResultSource(source);
      setShowDropdown(true);
    } catch {
      setResults([]);
      setQuality('low');
    } finally {
      if (intensify) {
        setIsIntensifying(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [proximity]);

  const handleIntensify = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (lastQueryRef.current.trim().length >= 3) {
      performSearch(lastQueryRef.current, true);
    }
  }, [performSearch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChangeRef.current(newValue);
    lastQueryRef.current = newValue;
    setResultSource('free');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSearch(newValue);
    }, 300);
  };

  const formatAddress = (props: PhotonFeature['properties']): string => {
    const parts: string[] = [];
    const street = props.street || (props.name && props.housenumber ? props.name : undefined);
    const placeName = props.name && !street ? props.name : undefined;
    if (street) parts.push(props.housenumber ? `${props.housenumber} ${street}` : street);
    if (placeName) parts.push(placeName);
    if (props.postcode) parts.push(props.postcode);
    if (props.city) parts.push(props.city);
    if (props.state) parts.push(props.state);
    if (props.country) parts.push(props.country);
    return parts.join(', ');
  };

  const handleSelect = (feature: PhotonFeature) => {
    const props = feature.properties;
    const name = props.name || props.street || value;
    // Prefer display_name (from Nominatim) for the full address, otherwise build from parts
    const address = props.display_name || formatAddress(props);
    const lat = String(feature.geometry.coordinates[1]);
    const lng = String(feature.geometry.coordinates[0]);
    // Set input to the full address so users see exactly what was selected
    onChangeRef.current(address || name);
    onPlaceSelectRef.current({ name, address, lat, lng });
    setResults([]);
    setShowDropdown(false);
  };

  const handleClear = () => {
    onClear();
    setResults([]);
    setShowDropdown(false);
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
        onBlur={() => { setTimeout(() => setShowDropdown(false), 200); }}
        placeholder={placeholder}
        className={`w-full pl-9 pr-8 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm ${
          isDark
            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
            : 'bg-white border-gray-300 text-gray-900'
        }`}
      />
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      {value ? (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          <X className="w-3.5 h-3.5 text-gray-400" />
        </button>
      ) : isLoading ? (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400 pointer-events-none" />
      ) : null}

      {showDropdown && (results.length > 0 || isIntensifying) && (
        <div
          className={`absolute z-[99999] mt-1 w-full rounded-lg border shadow-lg max-h-60 overflow-y-auto ${
            isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
          }`}
        >
          {results.map((feature, idx) => {
            const props = feature.properties;
            const name = props.name || props.street || 'Unknown';
            const address = props.display_name || formatAddress(props);
            return (
              <button
                key={idx}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(feature); }}
                className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 ${
                  isDark
                    ? 'border-gray-600 hover:bg-gray-600'
                    : 'border-gray-100 hover:bg-gray-100'
                }`}
              >
                <div className={`font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{name}</div>
                {address && (
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{address}</div>
                )}
              </button>
            );
          })}
          {isIntensifying && (
            <div className={`px-3 py-2 text-xs flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <Loader2 className="w-3 h-3 animate-spin" />
              Searching deeper...
            </div>
          )}
          {resultSource === 'free' && !isIntensifying && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleIntensify(); }}
              className={`w-full text-left px-3 py-2 text-sm border-t flex items-center gap-2 transition-colors ${
                quality === 'low'
                  ? (isDark ? 'bg-blue-900/30 hover:bg-blue-900/50 text-blue-300 border-gray-600' : 'bg-blue-50 hover:bg-blue-100 text-blue-600 border-gray-200')
                  : (isDark ? 'bg-gray-800 hover:bg-gray-750 text-gray-400 border-gray-600' : 'bg-gray-50 hover:bg-gray-100 text-gray-500 border-gray-200')
              }`}
            >
              <Sparkles className={`w-3.5 h-3.5 ${quality === 'low' ? 'animate-pulse' : ''}`} />
              <span>Search deeper{quality === 'low' ? ' — better results likely' : ''}</span>
            </button>
          )}
        </div>
      )}
      {showDropdown && results.length === 0 && !isIntensifying && !isLoading && quality === 'low' && lastQueryRef.current.trim().length >= 3 && (
        <div
          className={`absolute z-[99999] mt-1 w-full rounded-lg border shadow-lg ${
            isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
          }`}
        >
          <div className={`px-3 py-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            No results found.
          </div>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); handleIntensify(); }}
            className={`w-full text-left px-3 py-2 text-sm border-t flex items-center gap-2 transition-colors ${
              isDark ? 'bg-blue-900/30 hover:bg-blue-900/50 text-blue-300 border-gray-600' : 'bg-blue-50 hover:bg-blue-100 text-blue-600 border-gray-200'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 animate-pulse" />
            <span>Search deeper</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;

// ============================================================================
// CUSTOM LOCATION RESULT DISPLAY
// Shows the selected custom location with name, address, and coordinates.
// ============================================================================
interface CustomLocationResultProps {
  location: CustomLocationData;
  onClear: () => void;
  isDark?: boolean;
}

export function CustomLocationResult({ location, onClear, isDark = false }: CustomLocationResultProps) {
  return (
    <div className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border ${
      isDark ? 'bg-green-900/20 border-green-700/60' : 'bg-green-50 border-green-200'
    }`}>
      <MapPin className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
          {location.name}
        </div>
        {location.address && (
          <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {location.address}
          </div>
        )}
        {location.lat && location.lng && (
          <div className={`text-[11px] mt-1 font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {parseFloat(location.lat).toFixed(6)}, {parseFloat(location.lng).toFixed(6)}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onClear}
        className="p-1 rounded hover:bg-black/10 flex-shrink-0"
      >
        <X className="w-3.5 h-3.5 text-gray-400" />
      </button>
    </div>
  );
}
